import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

function ActionBar({ totalBaseCost, buttons }) {
    // Utility function to format currency
    const formatCurrency = (value) => { return Number(value).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ','); };

    useEffect(() => {
        const handlePadding = () => {
            const isDesktop = window.matchMedia('(min-width: 767px)').matches;
            if (isDesktop && totalBaseCost > 0) {
                document.body.style.paddingBottom = '104px';
            } else {
                document.body.style.paddingBottom = '';
            }
        };

        handlePadding();

        const resizeListener = () => handlePadding();
        window.addEventListener('resize', resizeListener);

        return () => {
            window.removeEventListener('resize', resizeListener);
            document.body.style.paddingBottom = ''; 
        };
    }, [totalBaseCost]);

    return (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-sm md:shadow-lg p-4 md:p-8 z-50">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between">
                {/* Estimate Total */}
                <div className="flex items-center justify-center md:justify-start w-full md:w-auto mb-1 md:mb-0 space-x-2">
                    <span className="text-sm md:text-base font-semibold text-gray-700">
                        Estimate Total:
                    </span>
                    <span className="text-lg md:text-xl font-semibold text-gray-900">
                        ${formatCurrency(totalBaseCost)}
                    </span>
                    <span className="text-sm md:text-base text-gray-500">+ tax</span>
                </div>

                {/* Dynamic Action Buttons */}
                <div className="flex flex-row items-center justify-center gap-1 md:gap-4 w-full md:w-auto">
                    {buttons && Array.isArray(buttons) && buttons.length > 0 ? (
                        buttons.map((button, index) => (
                            <button
                                key={index}
                                onClick={button.onClick}
                                disabled={button.disabled}
                                className={`${button.primary
                                    ? 'bg-[#699357] text-white hover:bg-[#577c47]'
                                    : 'text-[#699357] hover:text-[#577c47]'
                                    } text-nowrap text-base font-medium py-2 px-2 md:px-6 rounded-[5px] w-fit ${button.disabled ? 'opacity-50 cursor-not-allowed' : ''
                                    } transition-colors duration-200 flex items-center ${button.customClass || ''}`}
                                aria-disabled={button.disabled}
                                aria-label={button.ariaLabel || button.label}
                                title={button.title || button.label}
                            >
                                {button.label}
                                {button.svg === 'up-right arrow' && (
                                    <svg
                                        className="ml-[16px]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 25 24"
                                        fill="none"
                                    >
                                        <g clipPath="url(#clip0_2416_30405)">
                                            <path
                                                d="M18.9596 5.0415L6.04297 17.9582"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M7.33203 5.0415H18.957V16.6665"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2416_30405">
                                                <rect
                                                    width="24"
                                                    height="24"
                                                    fill="white"
                                                    transform="translate(0.5)"
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                )}
                            </button>
                        ))
                    ) : (
                        <span className="text-sm text-gray-500">No actions available</span>
                    )}
                </div>
            </div>
        </div>
    );
}

// Define default props to prevent undefined scenarios
ActionBar.defaultProps = {
    buttons: [],
};

// PropTypes for type checking and better maintainability
ActionBar.propTypes = {
    totalBaseCost: PropTypes.number.isRequired,
    buttons: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            onClick: PropTypes.func.isRequired,
            primary: PropTypes.bool, // Determines button styling
            disabled: PropTypes.bool,
            ariaLabel: PropTypes.string,
            title: PropTypes.string,
            customClass: PropTypes.string, // Optional for additional styling
            svg: PropTypes.string,
        })
    ),
};

export default ActionBar;
